/**
 * AASB 136 — Impairment of Assets. Rulepack v1.0.0.
 * Validates carrying vs recoverable amount; recoverable = max(FVLCD, VIU).
 * Goodwill annual-test reminder + indicator-based assessment.
 */
import { Finding, ValidationResult, DISCLAIMER } from './types';
import {
  CellValue, parseNumber, parseBool, makeHeaderMap, normaliseHeader,
  acceptedHeadersFor, findingBuilder, severityCounts, compliantCount, fmt,
} from './_shared';

const VERSION = 'aasb136-1.0.0';
const fb = findingBuilder('AASB_136' as any, VERSION);

const HMAP = makeHeaderMap([
  ['asset_id', 'asset id', 'cgu id', 'id'],
  ['asset_class', 'asset class', 'class', 'category'],
  ['cgu', 'cgu', 'cash generating unit', 'cash-generating unit'],
  ['carrying_amount', 'carrying amount', 'carrying value', 'nbv'],
  ['recoverable_amount', 'recoverable amount', 'recoverable'],
  ['fvlcd', 'fvlcd', 'fair value less costs to dispose', 'fair value'],
  ['viu', 'viu', 'value in use'],
  ['impairment_recognised', 'impairment recognised', 'impairment loss', 'impairment'],
  ['has_indicator', 'has indicator', 'impairment indicator', 'indicator'],
  ['is_goodwill', 'is goodwill', 'goodwill flag', 'goodwill'],
]);

const REQUIRED = ['asset_id', 'carrying_amount'];

export function detectAASB136(headers: string[]) {
  const hits = new Set<string>();
  for (const r of headers) { const c = normaliseHeader(HMAP, String(r ?? '')); if (c) hits.add(c); }
  const matched = REQUIRED.filter(r => hits.has(r));
  // Boost if recoverable_amount or impairment in headers
  const boost = ['recoverable', 'impairment', 'cgu', 'fvlcd', 'viu'].some(k => headers.some(h => String(h ?? '').toLowerCase().includes(k)));
  return {
    confidence: Math.min(1, (matched.length / REQUIRED.length) * (boost ? 1.5 : 1)),
    matched, missing: REQUIRED.filter(r => !hits.has(r)),
  };
}

export function validateAASB136(headers: string[], data: CellValue[][], _asAt: Date = new Date()): ValidationResult {
  const findings: Finding[] = [];
  const schemaErrors: string[] = [];
  const idx: Record<string, number> = {};
  headers.forEach((h, i) => { const c = normaliseHeader(HMAP, String(h ?? '')); if (c) idx[c] = i; });
  for (const f of REQUIRED) if (idx[f] === undefined) schemaErrors.push(`Missing column "${f}". Accepted: ${acceptedHeadersFor(HMAP, f).join(', ')}.`);
  if (schemaErrors.length) {
    return { summary: { standard: 'AASB_136' as any, rulepackVersion: VERSION, rowsTested: 0, compliant: 0, byseverity: { info: 0, warning: 0, error: 0 }, runAt: new Date().toISOString() }, findings, schemaErrors, disclaimer: DISCLAIMER };
  }

  const rowIds: string[] = [];
  data.forEach((raw, i) => {
    const rowNum = i + 2;
    const get = (k: string) => raw[idx[k]];
    const id = String(get('asset_id') ?? `row-${rowNum}`);
    rowIds.push(id);

    const missing = REQUIRED.filter(f => { const v = get(f); return v == null || v === ''; });
    if (missing.length) { findings.push(fb.build('AASB136.R01', 'error', 'Required field missing', `Row ${rowNum} (${id}): missing ${missing.join(', ')}.`, { rowId: id })); return; }

    const carrying = parseNumber(get('carrying_amount'));
    const recoverable = idx.recoverable_amount !== undefined ? parseNumber(get('recoverable_amount')) : null;
    const fvlcd = idx.fvlcd !== undefined ? parseNumber(get('fvlcd')) : null;
    const viu = idx.viu !== undefined ? parseNumber(get('viu')) : null;
    const impairment = idx.impairment_recognised !== undefined ? parseNumber(get('impairment_recognised')) : null;
    const indicator = idx.has_indicator !== undefined ? parseBool(get('has_indicator')) : null;
    const isGoodwill = idx.is_goodwill !== undefined ? parseBool(get('is_goodwill')) : false;

    if (!(carrying !== null && carrying > 0)) { findings.push(fb.build('AASB136.R02', 'error', 'Carrying amount must be positive', `Asset ${id}: $${fmt(carrying ?? 0)}.`, { rowId: id })); return; }

    // R03 recoverable = max(FVLCD, VIU)
    let recoverableExpected = recoverable;
    if (fvlcd !== null && viu !== null) {
      recoverableExpected = Math.max(fvlcd, viu);
      if (recoverable !== null) {
        const tol = Math.max(50, 0.005 * carrying!);
        if (Math.abs(recoverable - recoverableExpected) > tol) {
          findings.push(fb.build('AASB136.R03', 'warning', 'Recoverable amount differs from max(FVLCD, VIU)',
            `Asset ${id}: reported recoverable $${fmt(recoverable)}, expected $${fmt(recoverableExpected)} (max of FVLCD $${fmt(fvlcd)} and VIU $${fmt(viu)}). AASB 136.18.`,
            { rowId: id, evidence: { reported: recoverable, expected: recoverableExpected } }));
        }
      }
    } else if (recoverable === null && (fvlcd !== null || viu !== null)) {
      recoverableExpected = fvlcd !== null ? (viu !== null ? Math.max(fvlcd, viu) : fvlcd) : viu;
    }

    // R04 impairment trigger
    if (recoverableExpected !== null && carrying! > recoverableExpected) {
      const calculated = carrying! - recoverableExpected;
      const reported = impairment ?? 0;
      const tol = Math.max(50, 0.005 * carrying!);
      if (Math.abs(reported - calculated) > tol) {
        findings.push(fb.build('AASB136.R04', 'error', 'Impairment loss not recognised correctly',
          `Asset ${id}: carrying $${fmt(carrying!)} > recoverable $${fmt(recoverableExpected)}; required impairment $${fmt(calculated)}, reported $${fmt(reported)}. AASB 136.59 requires the carrying amount to be reduced to the recoverable amount.`,
          { rowId: id, evidence: { carrying, recoverable: recoverableExpected, requiredImpairment: calculated, reportedImpairment: reported }, suggestedAdjustment: { field: 'impairment_recognised', from: reported, to: +calculated.toFixed(2), rationale: 'Carrying − recoverable.' } }));
      }
    }

    // R05 goodwill annual test
    if (isGoodwill) {
      findings.push(fb.build('AASB136.R05', 'info', 'Goodwill — annual impairment test required',
        `Asset ${id} is goodwill. AASB 136.10 requires goodwill to be tested for impairment annually regardless of indicators, and at the same time each year.`,
        { rowId: id }));
    }

    // R06 indicator-driven test
    if (indicator === true && recoverable === null && fvlcd === null && viu === null) {
      findings.push(fb.build('AASB136.R06', 'warning', 'Indicator present but no recoverable test performed',
        `Asset ${id}: has_indicator=true but recoverable / FVLCD / VIU not provided. AASB 136.9 requires recoverable amount to be estimated when an impairment indicator exists.`,
        { rowId: id }));
    }

    // R07 indicators that should trigger an annual test
    if (indicator === true && impairment === 0) {
      findings.push(fb.build('AASB136.R07', 'info', 'Indicator present, no impairment recognised',
        `Asset ${id}: indicator flagged but no impairment recognised. Confirm the recoverable-amount calc supports no write-down (document inputs and assumptions).`,
        { rowId: id }));
    }
  });

  return {
    summary: { standard: 'AASB_136' as any, rulepackVersion: VERSION, rowsTested: rowIds.length, compliant: compliantCount(rowIds, findings), byseverity: severityCounts(findings), runAt: new Date().toISOString() },
    findings, disclaimer: DISCLAIMER,
  };
}
